import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'

import type {
  IssuesFeedSort,
  UpdatesFeedSort,
} from 'features/trend-detection/features/issue-feed/sort-types'
import type { TrendDetectionService } from 'features/trend-detection/service'
import type {
  AgentFeedOption,
  IssueFeedPeriod,
  TrendFeedBannerAgent,
  TrendFeedIssueItem,
  TrendFeedSectionIssues,
  TrendFeedSectionUpdates,
  TrendFeedUpdateItem,
} from 'features/trend-detection/types'

const FEED_LIMIT = 15

export interface UseIssueFeedResult {
  filterAgentUuid: string | null
  setFilterAgentUuid: (uuid: string | null) => void
  issuesSort: IssuesFeedSort
  updatesSort: UpdatesFeedSort
  bannerAgents: TrendFeedBannerAgent[]
  agentsForFilter: AgentFeedOption[]
  issuesItems: TrendFeedIssueItem[]
  updatesItems: TrendFeedUpdateItem[]
  issuesTotalCount: number | null
  updatesTotalCount: number | null
  issuesPage: number
  updatesPage: number
  issuesTotalPages: number
  updatesTotalPages: number
  setIssuesPage: (page: number) => void
  setUpdatesPage: (page: number) => void
  onIssuesPageChange: (page: number) => void
  onUpdatesPageChange: (page: number) => void
  setIssuesItems: React.Dispatch<React.SetStateAction<TrendFeedIssueItem[]>>
  setUpdatesItems: React.Dispatch<React.SetStateAction<TrendFeedUpdateItem[]>>
  issuesFeedLoading: boolean
  updatesFeedLoading: boolean
  onIssuesSortChange: (sort: IssuesFeedSort) => void
  onUpdatesSortChange: (sort: UpdatesFeedSort) => void
  refetchFeed: () => Promise<void>
  dataUpdatedAt: number
  isRefetching: boolean
  // Period filter — current vs all-time. Driven by the toolbar select and the
  // "View previous issues" banner; in all-time mode the issues query unions
  // historical runs server-side.
  period: IssueFeedPeriod
  onPeriodChange: (period: IssueFeedPeriod) => void
  sharplyElevatedCount: number
  /** True when there are historical (non-current) runs the user could view by switching to all-time. */
  hasHistoricalIssues: boolean
  // Emergence level filter (frontend filter chip, independent of agent sensitivity)
  emergenceLevelFilter: string | null
  onEmergenceLevelFilterChange: (level: string | null) => void
}

/**
 * Feed query + filter/sort/page loading. Skeleton when list is empty and fetching.
 * Issues and updates use separate queries so changing sort/page on one tab does not
 * refetch or replace the other section.
 *
 * A lightweight `/feed/meta` query runs first to compute shared state (banners,
 * agent list, run IDs, tracked IDs) once. Section-specific `/feed` calls then
 * pass the pre-computed IDs so the server skips redundant lookups.
 *
 * The feed surface is read-only with respect to tracking. Track/Untrack lives on
 * the insight detail page and the agent-detail page; those mutation sites
 * invalidate the trend-feed queries so the Tracked tab refetches with the new
 * shape on next render. No client-side overlay primitive needed.
 */
export function useIssueFeed(
  service: TrendDetectionService,
  projectId: string | undefined,
  feedTab: 'detected' | 'tracked' = 'detected',
  initialBannerAgents?: TrendFeedBannerAgent[],
  // Deep-link seeds; subsequent changes go through the setters below.
  initialFilterAgentUuid: string | null = null,
  initialPeriod: IssueFeedPeriod = 'current',
): UseIssueFeedResult {
  const queryClient = useQueryClient()
  const [filterAgentUuid, setFilterAgentUuidRaw] = useState<string | null>(
    initialFilterAgentUuid,
  )
  const [issuesSort, setIssuesSort] = useState<IssuesFeedSort>('emergence')
  const [updatesSort, setUpdatesSort] =
    useState<UpdatesFeedSort>('most_changing')
  const [issuesPage, setIssuesPage] = useState(1)
  const [updatesPage, setUpdatesPage] = useState(1)
  const [period, setPeriod] = useState<IssueFeedPeriod>(initialPeriod)
  const [emergenceLevelFilter, setEmergenceLevelFilter] = useState<
    string | null
  >(null)

  const prevBannerRef = useRef<
    Map<string, { name: string; status: 'running' | 'failed' }>
  >(new Map())
  const prevBannerProjectRef = useRef<string | undefined>(undefined)

  // Optimistic banners passed from the create/edit flow via location.state.
  // Shown immediately; dropped once the server includes the same agent_uuid.
  const [pendingBanners, setPendingBanners] = useState<TrendFeedBannerAgent[]>(
    () => initialBannerAgents ?? [],
  )

  // Track whether tabs have been opened at least once (lazy-load data on first visit).
  // "detected" is the default tab, so it starts activated.
  const [issuesActivated, setIssuesActivated] = useState(true)
  const [updatesActivated, setUpdatesActivated] = useState(false)
  useEffect(() => {
    if (feedTab === 'detected' && !issuesActivated) {
      setIssuesActivated(true)
    }
    if (feedTab === 'tracked' && !updatesActivated) {
      setUpdatesActivated(true)
    }
  }, [feedTab, issuesActivated, updatesActivated])

  const prevProjectIdRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!projectId) return
    if (prevProjectIdRef.current === undefined) {
      prevProjectIdRef.current = projectId
      return
    }
    if (prevProjectIdRef.current === projectId) return
    prevProjectIdRef.current = projectId
    setFilterAgentUuidRaw(null)
    setIssuesPage(1)
    setUpdatesPage(1)
    setPeriod('current')
    setIssuesTotalCount(null)
    setUpdatesTotalCount(null)
    setIssuesTotalPages(0)
    setUpdatesTotalPages(0)
    setEmergenceLevelFilter(null)
    setIssuesActivated(feedTab === 'detected')
    setUpdatesActivated(feedTab === 'tracked')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only on project change, not tab switch
  }, [projectId])

  const [issuesTotalCount, setIssuesTotalCount] = useState<number | null>(null)
  const [updatesTotalCount, setUpdatesTotalCount] = useState<number | null>(
    null,
  )
  const [issuesTotalPages, setIssuesTotalPages] = useState(0)
  const [updatesTotalPages, setUpdatesTotalPages] = useState(0)

  // Wrap setFilterAgentUuid so all resets happen in the same React batch —
  // no render gap where stale counts/data could flash through.
  const setFilterAgentUuid = useCallback(
    (uuid: string | null) => {
      setFilterAgentUuidRaw(uuid)
      setIssuesPage(1)
      setUpdatesPage(1)
      setIssuesTotalCount(null)
      setUpdatesTotalCount(null)
      setIssuesTotalPages(0)
      setUpdatesTotalPages(0)
      setEmergenceLevelFilter(null)
      setIssuesActivated(feedTab === 'detected')
      setUpdatesActivated(feedTab === 'tracked')
    },
    [feedTab],
  )

  // ── Status query: project-level, stable across filter changes ─────────
  const feedStatusQueryKey = ['trend-feed-status', projectId] as const

  const { data: feedStatus, refetch: refetchFeedStatus } = useQuery({
    queryKey: feedStatusQueryKey,
    queryFn: async () => {
      if (!projectId) throw new Error('no project')
      return service.fetchFeedStatus(projectId)
    },
    enabled: !!projectId,
  })

  // ── Counts query: filter-specific, changes with agent filter + period ────
  const feedCountsQueryKey = [
    'trend-feed-counts',
    projectId,
    filterAgentUuid,
    emergenceLevelFilter,
    period,
  ] as const

  const { data: feedCounts, refetch: refetchFeedCounts } = useQuery({
    queryKey: feedCountsQueryKey,
    queryFn: async () => {
      if (!projectId) throw new Error('no project')
      return service.fetchFeedCounts(
        projectId,
        filterAgentUuid ?? undefined,
        emergenceLevelFilter ?? undefined,
        period,
      )
    },
    enabled: !!projectId,
  })

  // ── Section queries ─────────────────────────────────────────────────
  // feedCounts is in the key so that when server state changes (run
  // starts/completes → counts refetch → last_run_completed_at changes),
  // react-query treats it as a new query: data goes to undefined,
  // skeleton shows, fresh fetch fires.
  const issuesQueryKey = [
    'trend-feed-issues',
    projectId,
    filterAgentUuid,
    issuesPage,
    issuesSort,
    emergenceLevelFilter,
    period,
    feedCounts,
  ] as const

  const updatesQueryKey = [
    'trend-feed-updates',
    projectId,
    filterAgentUuid,
    updatesPage,
    updatesSort,
    feedCounts,
  ] as const

  const agentUuidParam = filterAgentUuid ?? undefined

  const {
    data: issuesData,
    isFetching: issuesIsFetching,
    dataUpdatedAt: issuesDataUpdatedAt,
  } = useQuery({
    queryKey: issuesQueryKey,
    queryFn: async () => {
      if (!projectId || !feedCounts) throw new Error('no project or counts')
      return service.fetchFeedIssues(projectId, {
        page: issuesPage,
        limit: FEED_LIMIT,
        sort: issuesSort,
        agentUuid: agentUuidParam,
        emergenceLevel: emergenceLevelFilter ?? undefined,
        period,
      })
    },
    enabled: !!projectId && !!feedCounts && issuesActivated,
  })

  const {
    data: updatesData,
    isFetching: updatesIsFetching,
    dataUpdatedAt: updatesDataUpdatedAt,
  } = useQuery({
    queryKey: updatesQueryKey,
    queryFn: async () => {
      if (!projectId || !feedCounts) throw new Error('no project or counts')
      return service.fetchFeedUpdates(projectId, {
        page: updatesPage,
        limit: FEED_LIMIT,
        sort: updatesSort,
        agentUuid: agentUuidParam,
      })
    },
    enabled: !!projectId && !!feedCounts && updatesActivated,
  })

  // Update tab badge counts whenever the counts endpoint returns new data.
  // Section data effects below will overwrite with per-page counts when they
  // arrive, but feedCounts keeps badges accurate for inactive tabs.
  useEffect(() => {
    if (!feedCounts) return
    setIssuesTotalCount(feedCounts.issues_total_count ?? 0)
    setUpdatesTotalCount(feedCounts.updates_total_count ?? 0)
  }, [feedCounts])

  useEffect(() => {
    if (issuesData) {
      setIssuesTotalCount(issuesData.total_count ?? 0)
      setIssuesTotalPages(issuesData.total_pages ?? 0)
    }
  }, [issuesData])

  useEffect(() => {
    if (updatesData) {
      setUpdatesTotalCount(updatesData.total_count ?? 0)
      setUpdatesTotalPages(updatesData.total_pages ?? 0)
    }
  }, [updatesData])

  // Meta is project-level (no filter in key) so these are stable across
  // agent filter changes — no flicker.
  const agentsForFilter = feedStatus?.agents_for_filter ?? []

  // Merge optimistic pending banners with server banners, deduplicating by agent_uuid.
  const bannerAgents = useMemo(() => {
    const server = feedStatus?.banner_agents ?? []
    const serverIds = new Set(server.map((b) => b.agent_uuid))
    const uniquePending = pendingBanners.filter(
      (b) => !serverIds.has(b.agent_uuid),
    )
    return [...uniquePending, ...server]
  }, [feedStatus?.banner_agents, pendingBanners])

  // ── Banner transition detection (single-writer for prevBannerRef) ───
  // This is the ONLY effect that reads or writes prevBannerRef, which
  // prevents path-dependent bugs where a separate reset effect could
  // clear the ref without this effect re-running to repopulate it.
  // When banner state changes (run starts/completes), refetch feedCounts.
  // Section queries include feedCounts in their key, so they automatically
  // re-fire with fresh data — no manual section refetches needed.
  // Also drops pending banners once the server includes them.
  useEffect(() => {
    if (!feedStatus || !projectId) return

    // On project change, seed the ref from the current status without
    // firing transitions — avoids false toasts and stale cross-project state.
    if (prevBannerProjectRef.current !== projectId) {
      prevBannerProjectRef.current = projectId
      prevBannerRef.current = new Map(
        feedStatus.banner_agents.map((b) => [
          b.agent_uuid,
          { name: b.agent_name, status: b.status },
        ]),
      )
      return
    }

    const nextBannerMap = new Map(
      feedStatus.banner_agents.map((b) => [b.agent_uuid, b] as const),
    )
    let changed = false
    // Detect completed runs (was running, now gone).
    for (const [agentUuid, info] of prevBannerRef.current) {
      if (!nextBannerMap.has(agentUuid) && info.status === 'running') {
        changed = true
      }
    }
    // Detect newly started runs (not in prev, now running).
    for (const [agentUuid, banner] of nextBannerMap) {
      if (
        banner.status === 'running' &&
        !prevBannerRef.current.has(agentUuid)
      ) {
        changed = true
      }
    }
    prevBannerRef.current = new Map(
      feedStatus.banner_agents.map((b) => [
        b.agent_uuid,
        { name: b.agent_name, status: b.status },
      ]),
    )
    // Drop pending banners that the server now includes.
    if (pendingBanners.length > 0) {
      const serverIds = new Set(nextBannerMap.keys())
      setPendingBanners((prev) =>
        prev.filter((b) => !serverIds.has(b.agent_uuid)),
      )
    }
    if (changed) {
      // Reset counts to null so loading skeletons show instead of stale
      // "0 issues" while the fresh counts are in-flight.
      setIssuesTotalCount(null)
      setUpdatesTotalCount(null)
      void refetchFeedCounts()
    }
  }, [feedStatus, projectId, refetchFeedCounts, pendingBanners.length])

  const issuesItems = useMemo(() => issuesData?.items ?? [], [issuesData])

  const updatesItems = useMemo(() => updatesData?.items ?? [], [updatesData])

  const setIssuesItems = useCallback(
    (updater: React.SetStateAction<TrendFeedIssueItem[]>) => {
      if (!projectId) return
      queryClient.setQueriesData<TrendFeedSectionIssues | undefined>(
        ['trend-feed-issues', projectId],
        (old) => {
          if (!old) return old
          const prev = old.items
          const next =
            typeof updater === 'function'
              ? (updater as (p: TrendFeedIssueItem[]) => TrendFeedIssueItem[])(
                  prev,
                )
              : updater
          return { ...old, items: next }
        },
      )
    },
    [projectId, queryClient],
  )

  const setUpdatesItems = useCallback(
    (updater: React.SetStateAction<TrendFeedUpdateItem[]>) => {
      if (!projectId) return
      queryClient.setQueriesData<TrendFeedSectionUpdates | undefined>(
        ['trend-feed-updates', projectId],
        (old) => {
          if (!old) return old
          const prev = old.items
          const next =
            typeof updater === 'function'
              ? (
                  updater as (p: TrendFeedUpdateItem[]) => TrendFeedUpdateItem[]
                )(prev)
              : updater
          return { ...old, items: next }
        },
      )
    },
    [projectId, queryClient],
  )

  // Refetch status + filter. Section queries auto-fire because
  // feedCounts is in their key — when it changes, they re-fetch.
  const refetchFeed = useCallback(async (): Promise<void> => {
    await Promise.all([refetchFeedStatus(), refetchFeedCounts()])
  }, [refetchFeedStatus, refetchFeedCounts])

  // New/completed runs are detected via the agent-events SSE stream
  // (`useAgentEventsStream`, mounted app-wide in ProjectLayout), which
  // invalidates `['trend-feed-status', projectId]` on each transition — there
  // is no short-interval poll; a dropped stream auto-reconnects.

  // Loading = we don't have data for the current query key yet, OR
  // count is null (transition in progress — "we don't know" is not "zero").
  // Without keepPreviousData, changing any key part (page, sort, filter)
  // clears data to undefined → skeleton shows automatically.
  const filterLoading = !!projectId && !feedCounts
  const issuesFeedLoadingResolved =
    filterLoading ||
    (issuesTotalCount === null && issuesActivated) ||
    (!issuesData && !!projectId && !!feedCounts && issuesActivated)
  const updatesFeedLoadingResolved =
    filterLoading ||
    (updatesTotalCount === null && updatesActivated) ||
    (!updatesData && !!projectId && !!feedCounts && updatesActivated)

  const dataUpdatedAt = Math.max(
    issuesDataUpdatedAt || 0,
    updatesDataUpdatedAt || 0,
  )
  const isRefetching = issuesIsFetching || updatesIsFetching

  const onIssuesSortChange = useCallback(
    (sort: IssuesFeedSort) => {
      if (sort === issuesSort) return
      setIssuesSort(sort)
      setIssuesPage(1)
    },
    [issuesSort],
  )

  const onUpdatesSortChange = useCallback(
    (sort: UpdatesFeedSort) => {
      if (sort === updatesSort) return
      setUpdatesSort(sort)
      setUpdatesPage(1)
    },
    [updatesSort],
  )

  const onIssuesPageChange = useCallback(
    (page: number) => {
      if (page === issuesPage) return
      setIssuesPage(page)
    },
    [issuesPage],
  )

  const onUpdatesPageChange = useCallback(
    (page: number) => {
      if (page === updatesPage) return
      setUpdatesPage(page)
    },
    [updatesPage],
  )

  const onPeriodChange = useCallback(
    (next: IssueFeedPeriod) => {
      if (next === period) return
      setPeriod(next)
      setIssuesPage(1)
      // Counts and issues both depend on period — clear so a skeleton shows
      // instead of a stale "current"-period count flashing through.
      setIssuesTotalCount(null)
      setIssuesTotalPages(0)
    },
    [period],
  )

  return {
    filterAgentUuid,
    setFilterAgentUuid,
    issuesSort,
    updatesSort,
    bannerAgents,
    agentsForFilter,
    issuesItems,
    updatesItems,
    issuesTotalCount,
    updatesTotalCount,
    issuesPage,
    updatesPage,
    issuesTotalPages,
    updatesTotalPages,
    setIssuesPage,
    setUpdatesPage,
    onIssuesPageChange,
    onUpdatesPageChange,
    setIssuesItems,
    setUpdatesItems,
    issuesFeedLoading: issuesFeedLoadingResolved,
    updatesFeedLoading: updatesFeedLoadingResolved,
    onIssuesSortChange,
    onUpdatesSortChange,
    refetchFeed,
    dataUpdatedAt,
    isRefetching,
    period,
    onPeriodChange,
    sharplyElevatedCount: feedCounts?.sharply_elevated_count ?? 0,
    hasHistoricalIssues: feedCounts?.has_historical_issues ?? false,
    emergenceLevelFilter,
    onEmergenceLevelFilterChange: (level: string | null) => {
      setEmergenceLevelFilter(level)
      setIssuesPage(1)
    },
  }
}
